import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Plus, Trash2, X } from 'lucide-react';
import type { AppModule, Role, RoleDef, User } from '../../types';
import { userService, type UserInput } from '../../services/userService';
import { moduleService } from '../../services/moduleService';
import { roleService } from '../../services/roleService';
import { PermissionChecklist } from './PermissionChecklist';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormState {
  name: string;
  email: string;
  password: string;
  role: Role;
  assignedModuleIds: string[];
}

const EMPTY_FORM: FormState = {
  name: '',
  email: '',
  password: '',
  role: 'user',
  assignedModuleIds: [],
};

function validate(form: FormState, isEditing: boolean): Partial<Record<keyof FormState, string>> {
  const errors: Partial<Record<keyof FormState, string>> = {};
  if (!form.name.trim()) errors.name = 'Name is required';
  if (!form.email.trim()) errors.email = 'Email is required';
  else if (!EMAIL_PATTERN.test(form.email.trim())) errors.email = 'Enter a valid email address';
  // Password is only required when creating; blank on edit means "unchanged".
  if (!isEditing && !form.password.trim()) errors.password = 'Password is required';
  else if (form.password.trim() && form.password.trim().length < 6)
    errors.password = 'Use at least 6 characters';
  return errors;
}

/** Admin-only. Rendering is guarded by SettingsPage via `can('users:manage')`. */
export default function UserManagementTab({ currentUserId }: { currentUserId: string }) {
  const [users, setUsers] = useState<User[]>([]);
  const [modules, setModules] = useState<AppModule[]>([]);
  const [roles, setRoles] = useState<RoleDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [nextUsers, nextModules, nextRoles] = await Promise.all([
        userService.listUsers(),
        Promise.resolve(moduleService.listAllModules()),
        roleService.list(),
      ]);
      setUsers(nextUsers);
      setModules(nextModules);
      setRoles(nextRoles);
    } catch {
      setLoadError('Could not load users. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const moduleTitleById = useMemo(
    () => new Map(modules.map((module) => [module.id, module.title || module.name])),
    [modules],
  );

  const roleById = useMemo(() => new Map(roles.map((role) => [role.id, role])), [roles]);
  const selectedRole = roleById.get(form.role);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setSubmitError(null);
    setShowForm(true);
  };

  const openEdit = (user: User) => {
    setEditingId(user.id);
    setForm({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      assignedModuleIds: [...user.assignedModuleIds],
    });
    setErrors({});
    setSubmitError(null);
    setShowForm(true);
  };

  const submit = async () => {
    const nextErrors = validate(form, editingId !== null);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: UserInput = {
        name: form.name,
        email: form.email,
        role: form.role,
        assignedModuleIds: form.assignedModuleIds,
        ...(form.password.trim() ? { password: form.password.trim() } : {}),
      };
      if (editingId) await userService.updateUser(editingId, payload);
      else await userService.createUser(payload);

      setShowForm(false);
      await load();
    } catch (err: any) {
      setSubmitError(err?.message || 'Could not save the user');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleModule = (moduleId: string) => {
    setForm((current) => ({
      ...current,
      assignedModuleIds: current.assignedModuleIds.includes(moduleId)
        ? current.assignedModuleIds.filter((id) => id !== moduleId)
        : [...current.assignedModuleIds, moduleId],
    }));
  };

  const toggleStatus = async (user: User) => {
    await userService.setUserStatus(user.id, user.status === 'active' ? 'disabled' : 'active');
    await load();
  };

  const remove = async (user: User) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete ${user.name}? This cannot be undone.`)) return;
    await userService.deleteUser(user.id);
    await load();
  };

  if (loading) {
    return <p className="py-10 text-center text-sm text-slate-400">Loading users…</p>;
  }

  if (loadError) {
    return (
      <div className="py-10 text-center">
        <p className="text-sm text-red-600">{loadError}</p>
        <button
          onClick={() => void load()}
          className="mt-3 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          {users.length} {users.length === 1 ? 'user' : 'users'}
        </p>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#0F2942] px-3 py-2 text-xs font-semibold text-white transition hover:bg-[#16395C]"
        >
          <Plus className="h-3.5 w-3.5" />
          New user
        </button>
      </div>

      {/* ---- Create / edit form ---- */}
      {showForm && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-[#0F2942]">
              {editingId ? 'Edit user' : 'Create user'}
            </h4>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              aria-label="Close form"
              className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Name *</span>
              <input
                value={form.name}
                onChange={(event) => setForm((c) => ({ ...c, name: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-400"
              />
              {errors.name && <span className="mt-1 block text-[11px] text-red-600">{errors.name}</span>}
            </label>

            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Email *</span>
              <input
                type="email"
                value={form.email}
                onChange={(event) => setForm((c) => ({ ...c, email: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-400"
              />
              {errors.email && <span className="mt-1 block text-[11px] text-red-600">{errors.email}</span>}
            </label>

            <label className="block">
              <span className="text-xs font-semibold text-slate-600">
                Password {editingId ? '(leave blank to keep)' : '*'}
              </span>
              <input
                type="password"
                value={form.password}
                onChange={(event) => setForm((c) => ({ ...c, password: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-400"
              />
              {errors.password && (
                <span className="mt-1 block text-[11px] text-red-600">{errors.password}</span>
              )}
            </label>

            <label className="block">
              <span className="text-xs font-semibold text-slate-600">Role *</span>
              <select
                value={form.role}
                onChange={(event) => setForm((c) => ({ ...c, role: event.target.value as Role }))}
                className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-teal-400"
              >
                {roles.length === 0 && <option value={form.role}>{form.role}</option>}
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* ---- What the selected role can do ---- */}
          {selectedRole && (
            <div className="mt-3">
              <span className="text-xs font-semibold text-slate-600">
                {selectedRole.name} can:
              </span>
              <div className="mt-1.5">
                <PermissionChecklist selected={selectedRole.permissions} />
              </div>
            </div>
          )}

          {/* ---- Module assignment ---- */}
          <div className="mt-3">
            <span className="text-xs font-semibold text-slate-600">
              Assigned modules
              {form.role === 'admin' && (
                <span className="ml-1 font-normal text-slate-400">
                  — admins can see every module regardless
                </span>
              )}
            </span>
            {modules.length === 0 ? (
              <p className="mt-1.5 rounded-lg border border-dashed border-slate-300 px-3 py-4 text-center text-xs text-slate-400">
                No modules exist yet. Create one from the home page first.
              </p>
            ) : (
              <div className="mt-1.5 max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2">
                {modules.map((module) => {
                  const selected = form.assignedModuleIds.includes(module.id);
                  return (
                    <button
                      key={module.id}
                      type="button"
                      onClick={() => toggleModule(module.id)}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition ${
                        selected ? 'bg-teal-50 text-[#0F2942]' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <span
                        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${
                          selected ? 'border-teal-500 bg-teal-500' : 'border-slate-300 bg-white'
                        }`}
                      >
                        {selected && <Check className="h-3 w-3 text-white" />}
                      </span>
                      <span className="truncate">{module.title || module.name}</span>
                      <span className="ml-auto flex-shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                        {module.visibility === 'user-owned' ? 'user' : 'admin'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {submitError && (
            <p role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
              {submitError}
            </p>
          )}

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => void submit()}
              disabled={submitting}
              className="rounded-lg bg-[#2DD4BF] px-4 py-2 text-xs font-semibold text-[#0B1F38] transition hover:bg-[#5EEAD4] disabled:opacity-50"
            >
              {submitting ? 'Saving…' : editingId ? 'Save changes' : 'Create user'}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ---- Users table ---- */}
      {users.length === 0 ? (
        <p className="gl-panel border-dashed py-10 text-center text-sm text-slate-400">
          No users yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2.5 font-semibold">Name</th>
                <th className="px-3 py-2.5 font-semibold">Email</th>
                <th className="px-3 py-2.5 font-semibold">Role</th>
                <th className="px-3 py-2.5 font-semibold">Modules</th>
                <th className="px-3 py-2.5 font-semibold">Status</th>
                <th className="px-3 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-t border-slate-100">
                  <td className="px-3 py-2.5 font-medium text-[#0F2942]">
                    {user.name}
                    {user.id === currentUserId && (
                      <span className="ml-1.5 text-[10px] text-slate-400">(you)</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-slate-600">{user.email}</td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                        user.role === 'admin'
                          ? 'bg-[#0F2942] text-white'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {roleById.get(user.role)?.name ?? user.role}
                    </span>
                  </td>
                  <td className="max-w-[180px] px-3 py-2.5 text-xs text-slate-500">
                    {user.role === 'admin'
                      ? 'All modules'
                      : user.assignedModuleIds.length === 0
                        ? '—'
                        : user.assignedModuleIds
                            .map((id) => moduleTitleById.get(id) ?? 'Unknown')
                            .join(', ')}
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                        user.status === 'active' ? 'text-teal-700' : 'text-slate-400'
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          user.status === 'active' ? 'bg-teal-500' : 'bg-slate-300'
                        }`}
                      />
                      {user.status === 'active' ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => openEdit(user)}
                        className="rounded px-2 py-1 text-[11px] font-medium text-blue-600 hover:bg-blue-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => void toggleStatus(user)}
                        disabled={user.id === currentUserId}
                        title={user.id === currentUserId ? 'You cannot disable your own account' : ''}
                        className="rounded px-2 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-30"
                      >
                        {user.status === 'active' ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void remove(user)}
                        disabled={user.id === currentUserId}
                        title={user.id === currentUserId ? 'You cannot delete your own account' : ''}
                        aria-label={`Delete ${user.name}`}
                        className="rounded p-1 text-red-500 hover:bg-red-50 disabled:opacity-30"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
